import { Injectable, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router, type ActivatedRouteSnapshot } from '@angular/router';
import type { ChatPromptContext, ChatPromptContextEntity } from '@flux/iara-core/prompt-context';
import { filter } from 'rxjs';
import {
  IARA_CARD_DETAIL_PANEL,
  IARA_TASK_PARAMS,
  IARA_VIEW_MODE_LABELS,
  type IaraPublishedEntity,
  type IaraPublishedLabel,
} from './iara-context.model';

/**
 * Fonte única de "onde o usuário está" para o contexto do chat da Iara.
 *
 * São duas origens, e a diferença importa:
 *
 * - **Derivada da URL** (fluxo, card, tarefa): o id vem do router, e a tela só
 *   publica o RÓTULO. Se o rótulo publicado não bate com o id da rota, é
 *   descartado — nunca mostramos o nome de um card enquanto a URL aponta pra outro.
 * - **Publicada por inteiro** (aprovação): o dialog não escreve query param, então
 *   id e rótulo vêm do publish e o ciclo de vida é responsabilidade da tela.
 *
 * Lê `queryParams` direto do snapshot em vez de usar `QuerifyService`: aquele
 * serviço também atualiza seu cache em `NavigationEnd`, e ler dele no mesmo evento
 * seria uma corrida de ordem de subscription.
 */
@Injectable({ providedIn: 'root' })
export class IaraContextService {
  private readonly router = inject(Router);

  /** Muda a cada navegação concluída, reavaliando rota e query params. */
  private readonly navigationTick = signal(0);

  private readonly pipeLabel = signal<IaraPublishedLabel | null>(null);
  private readonly cardLabel = signal<IaraPublishedLabel | null>(null);
  private readonly stageLabel = signal<string | null>(null);
  private readonly taskLabel = signal<IaraPublishedLabel | null>(null);
  private readonly approval = signal<IaraPublishedEntity | null>(null);

  public readonly context = computed<ChatPromptContext>(() => {
    this.navigationTick();

    const leaf = this.leafSnapshot();
    const queryParams: Record<string, unknown> = leaf.queryParams ?? {};

    return {
      // Ordem = especificidade. O consumidor toma a última como a "atual".
      entities: [
        ...this.pipeEntity(leaf),
        ...this.cardEntities(queryParams),
        ...this.taskEntity(queryParams),
        ...this.approvalEntity(),
      ],
      route: this.router.url.split('?')[0],
      screen: this.screenLabel(leaf, queryParams),
    };
  });

  public constructor() {
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        takeUntilDestroyed(),
      )
      .subscribe(() => this.navigationTick.update((tick) => tick + 1));
  }

  public publishPipeLabel(id: string, label: string): void {
    this.pipeLabel.set({ id, label });
  }

  public publishCardLabel(id: string, label: string, stageLabel?: string): void {
    this.cardLabel.set({ id, label });
    this.stageLabel.set(stageLabel ?? null);
  }

  public clearCardLabel(): void {
    this.cardLabel.set(null);
    this.stageLabel.set(null);
  }

  public publishTaskLabel(id: string, label: string): void {
    this.taskLabel.set({ id, label });
  }

  public clearTaskLabel(): void {
    this.taskLabel.set(null);
  }

  /** A aprovação não tem rastro na URL: a tela é dona do ciclo de vida dela. */
  public publishApproval(id: string, label?: string): void {
    this.approval.set({ id, label, type: 'approval' });
  }

  public clearApproval(): void {
    this.approval.set(null);
  }

  private pipeEntity(leaf: ActivatedRouteSnapshot): ChatPromptContextEntity[] {
    const params: Record<string, unknown> = leaf.params ?? {};
    const pipeId = typeof params['pipeId'] === 'string' ? params['pipeId'] : null;
    if (!pipeId) return [];

    return [this.withLabel({ id: pipeId, type: 'pipe' }, this.pipeLabel())];
  }

  private cardEntities(queryParams: Record<string, unknown>): ChatPromptContextEntity[] {
    const isCardPanel = queryParams['panel'] === IARA_CARD_DETAIL_PANEL;
    const cardId = typeof queryParams['cardId'] === 'string' ? queryParams['cardId'] : null;
    if (!isCardPanel || !cardId) return [];

    const published = this.cardLabel();
    const card = this.withLabel({ id: cardId, type: 'card' }, published);
    const stage = published?.id === cardId ? this.stageLabel() : null;

    // Etapa ANTES do card: ela é onde o card está, não o que o usuário está
    // olhando. Invertido, o chip mostraria "Etapa: A fazer" com um card aberto.
    return stage ? [{ label: stage, type: 'stage' }, card] : [card];
  }

  private taskEntity(queryParams: Record<string, unknown>): ChatPromptContextEntity[] {
    const taskId = IARA_TASK_PARAMS.map((param) => queryParams[param]).find(
      (value): value is string => typeof value === 'string' && value !== 'new',
    );
    if (!taskId) return [];

    return [this.withLabel({ id: taskId, type: 'task' }, this.taskLabel())];
  }

  private approvalEntity(): ChatPromptContextEntity[] {
    const approval = this.approval();

    return approval ? [approval] : [];
  }

  /**
   * Só aplica o rótulo quando ele foi publicado para ESTA entidade. Compara com um
   * `entity.id` já garantido — sem o teste explícito, uma entidade sem id casaria
   * `undefined === undefined`.
   */
  private withLabel(
    entity: ChatPromptContextEntity,
    published: IaraPublishedLabel | null,
  ): ChatPromptContextEntity {
    if (!published || !entity.id || published.id !== entity.id) return entity;

    return { ...entity, label: published.label };
  }

  /** Título resolvido da rota, sufixado com a aba quando a tela tem `view_mode`. */
  private screenLabel(
    leaf: ActivatedRouteSnapshot,
    queryParams: Record<string, unknown>,
  ): string | undefined {
    const title = typeof leaf.title === 'string' ? leaf.title : undefined;
    if (!title) return undefined;

    const viewMode = queryParams['view_mode'];
    const tab = typeof viewMode === 'string' ? IARA_VIEW_MODE_LABELS[viewMode] : undefined;

    return tab ? `${title} - ${tab}` : title;
  }

  private leafSnapshot(): ActivatedRouteSnapshot {
    let snapshot = this.router.routerState.snapshot.root;
    while (snapshot.firstChild) snapshot = snapshot.firstChild;
    return snapshot;
  }
}
